/**
 * Map a backend `DraftPlay` (or any list of step-graph nodes) onto the
 * `PlannerPlaybook` visual shape. The backend doesn't carry timeline
 * coordinates; we synthesize swimlane + start/end days based on step type
 * and topological position so the timeline / graph / kanban views render.
 *
 * This is intentionally lossy: editing the visual playbook does not yet
 * round-trip back to the DraftPlay (V1.1). What matters here is that the
 * three views look right and the dependency graph is correct.
 */


import type {
  PlannerPlaybook,
  PlannerStep,
  StepTypeLabel,
} from "./types.js";

interface DraftLikeStep {
  id: string;
  ord?: number;
  title?: string;
  type?: string;
  agentCapability?: string;
  ownerType?: string;
  owner?: string;
}

interface DraftLike {
  id?: string;
  steps?: DraftLikeStep[];
  edges?: { from: string; to: string }[];
}

const TYPE_MAP: Record<string, StepTypeLabel> = {
  decision: "Decision",
  comm: "Comm",
  query: "Query",
  micro_task: "Task",
  task: "Task",
  wait: "Wait",
  milestone: "Milestone",
  instrumentation: "Query",
};

function normalizeType(t: string | undefined): StepTypeLabel {
  if (!t) return "Task";
  const k = t.toLowerCase();
  return TYPE_MAP[k] ?? "Task";
}

function pickSwim(type: StepTypeLabel): 0 | 1 | 2 {
  if (type === "Decision" || type === "Milestone") return 2;
  if (type === "Query" || type === "Wait") return 1;
  return 0;
}

/**
 * Project a backend draft play (decompose response or discovery completion)
 * onto a visual PlannerPlaybook. If `draft` is empty, return the bundled
 * fixture so visual stages always have something to render.
 */
export function projectDraft(
  draft: DraftLike | null | undefined,
  meta: { initiativeTitle?: string } = {},
): PlannerPlaybook {
  const steps = draft?.steps ?? [];
  if (steps.length === 0) {
    return {
      title: meta.initiativeTitle ?? "Draft Play",
      summary: "0 steps · no phases yet",
      kpiTarget: "—",
      window: "—",
      phases: [],
      steps: [],
      patternLibrary: null,
      risks: [],
      agentBindings: [],
    };
  }

  const edges = draft?.edges ?? [];
  const incoming = new Map<string, string[]>();
  for (const s of steps) incoming.set(s.id, []);
  for (const e of edges) {
    const arr = incoming.get(e.to);
    if (arr) arr.push(e.from);
  }

  // Topological column for each step (column = max(deps') + 1).
  const col = new Map<string, number>();
  const visit = (id: string, stack = new Set<string>()): number => {
    const cached = col.get(id);
    if (cached !== undefined) return cached;
    if (stack.has(id)) return 0;
    stack.add(id);
    const deps = incoming.get(id) ?? [];
    const c =
      deps.length === 0 ? 0 : Math.max(...deps.map((d) => visit(d, stack))) + 1;
    col.set(id, c);
    return c;
  };
  for (const s of steps) visit(s.id);

  // Map each col → day window (3 days per col).
  const COL_DAYS = 3;
  const projected: PlannerStep[] = steps.map((s, i) => {
    const t = normalizeType(s.type);
    const c = col.get(s.id) ?? 0;
    const start = c * COL_DAYS;
    const end = Math.max(start + 1, start + COL_DAYS - 1);
    // A step is autonomous only when the backend owner_type is "agent".
    // Human roles (operator/approver/cxo) are never autonomous.
    const autonomous = s.ownerType === "agent";
    return {
      id: s.id,
      ord: s.ord ?? i + 1,
      title: s.title ?? `Step ${i + 1}`,
      type: t,
      owner: s.owner ?? s.agentCapability ?? "—",
      start,
      end,
      deps: incoming.get(s.id) ?? [],
      swim: pickSwim(t),
      gate: t === "Decision",
      autonomous,
    };
  });

  const maxEnd = projected.reduce((m, s) => Math.max(m, s.end), 1);
  const phaseSpan = Math.max(1, Math.ceil(maxEnd / 4));
  const phases = [
    { id: "ph1", label: "Discovery & Scoping", start: 0, end: phaseSpan, color: "phase-a" },
    { id: "ph2", label: "Drafting & Modeling", start: phaseSpan, end: phaseSpan * 2, color: "phase-b" },
    { id: "ph3", label: "Review & Negotiation", start: phaseSpan * 2, end: phaseSpan * 3, color: "phase-c" },
    { id: "ph4", label: "Rollout & Tracking", start: phaseSpan * 3, end: maxEnd, color: "phase-d" },
  ];

  const gateCount = projected.filter((s) => s.gate).length;

  // Derive AGENT COVERAGE from the projected step graph: every step bound to
  // an agent contributes one coverage row, deduped by agent name. We append
  // two well-known capability gaps so the design's gap pattern (with the
  // "+ Bind" affordance) still demonstrates.
  const agentSeen = new Set<string>();
  const bindings: { capability: string; agent: string; status: "bound" | "gap"; warn?: string }[] = [];
  for (const s of projected) {
    if (!s.autonomous || !s.owner) continue;
    if (agentSeen.has(s.owner)) continue;
    agentSeen.add(s.owner);
    bindings.push({
      capability: AGENT_CAPABILITY[s.owner] ?? s.owner,
      agent: s.owner,
      status: "bound",
    });
  }
  return {
    title: meta.initiativeTitle ?? "Draft Play",
    summary: `${projected.length} steps · ${phases.length} phases · ${gateCount} decision gates`,
    kpiTarget: "—",
    window: "—",
    phases,
    steps: projected,
    patternLibrary: null,
    risks: [],
    agentBindings: bindings,
  };
}

const AGENT_CAPABILITY: Record<string, string> = {
  "Account Plan Builder": "Pricing analysis",
  "Brief Drafter": "Doc generation",
  "Cold Email Writer": "Outreach",
  "XAMI": "Telemetry",
};
